// garuda - Pencils (Fragments) Page

import React, { useState, useEffect } from "react";
import { useNavigation } from "../context/NavigationContext";
import { HashtagBg } from "../components/Icons";
import MarkdownRenderer from "../components/MarkdownRenderer";
import { formatTimestamp } from "../utils/helpers";
import JackpotWord from "../components/JackpotWord";
import { fetchAllContent } from "../services/contentService";
import PageNavigation from "../components/PageNavigation";
import "./Pencils.css";

const Pencils = () => {
  const { navigateTo } = useNavigation();
  const [pencils, setPencils] = useState([]);
  const [filteredPencils, setFilteredPencils] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPencil, setSelectedPencil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isReadingMode, setIsReadingMode] = useState(true);

  // Load content from files on mount
  useEffect(() => {
    const loadPencils = async () => {
      setLoading(true);
      const content = await fetchAllContent("pencils");
      setPencils(content);
      setFilteredPencils(content);
      setLoading(false);
    };
    loadPencils();
  }, []);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPencils(pencils);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = pencils.filter(
      (pencil) =>
        pencil.title.toLowerCase().includes(query) ||
        (pencil.tags &&
          pencil.tags.some((tag) => tag.toLowerCase().includes(query))),
    );
    setFilteredPencils(filtered);
  }, [searchQuery, pencils]);

  const handlePencilClick = (pencil) => {
    setSelectedPencil(pencil);
  };

  const toggleReadingMode = () => {
    setIsReadingMode(!isReadingMode);
  };

  // Apply reading mode class to body
  useEffect(() => {
    if (isReadingMode) {
      document.body.classList.add("reading-mode");
    } else {
      document.body.classList.remove("reading-mode");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("reading-mode");
    };
  }, [isReadingMode]);

  return (
    <div className="pencils-container">
      <PageNavigation
        isReadingMode={isReadingMode}
        onToggleReadingMode={toggleReadingMode}
        showReadingMode={!!selectedPencil}
      />
      <HashtagBg opacity={0.05} offset={{ x: "5%", y: "10%" }} />

      <div className="pencils-header">
        <h1 className="page-title">
          <JackpotWord onClick={() => navigateTo("home")}>Pencils</JackpotWord>
        </h1>
        <p className="page-subtitle">
          Fragments of thought, scattered across time.
        </p>
      </div>

      <div className="pencils-content">
        {/* List Section */}
        <div className="pencils-list-section">
          {/* Search Bar */}
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="search fragments_"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Pencils List */}
          <div className="pencils-list">
            {loading ? (
              <p className="no-results">Loading fragments...</p>
            ) : filteredPencils.length === 0 ? (
              <p className="no-results">No fragments found.</p>
            ) : (
              filteredPencils.map((pencil) => (
                <div
                  key={pencil.id}
                  className={`pencil-item ${
                    selectedPencil?.id === pencil.id ? "active" : ""
                  }`}
                  onClick={() => handlePencilClick(pencil)}
                >
                  <div className="pencil-item-header">
                    <h3 className="pencil-title">{pencil.title}</h3>
                  </div>
                  <p className="pencil-timestamp">
                    {formatTimestamp(pencil.timestamp)}
                  </p>
                  {pencil.tags && pencil.tags.length > 0 && (
                    <div className="pencil-tags">
                      {pencil.tags.map((tag, idx) => (
                        <span key={idx} className="tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Detail Section */}
        <div className="pencils-detail-section">
          {!selectedPencil ? (
            <div className="empty-state">
              <p>Select a fragment to read.</p>
            </div>
          ) : (
            <div className="detail-content">
              <h2 className="detail-title">{selectedPencil.title}</h2>
              <div className="author-section">
                <img
                  src="/images/profile.jpg"
                  alt="Prem Agarwal"
                  className="author-avatar"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className="author-avatar author-avatar-fallback"
                  style={{ display: "none" }}
                >
                  PA
                </div>
                <div className="author-info">
                  <div className="author-name">Prem Agarwal</div>
                  <div className="author-timestamp">
                    {formatTimestamp(selectedPencil.timestamp)}
                  </div>
                </div>
              </div>
              {selectedPencil.tags && selectedPencil.tags.length > 0 && (
                <div className="detail-tags">
                  {selectedPencil.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <MarkdownRenderer content={selectedPencil.content} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pencils;
